//Libraries
import { get } from 'lodash';
import { styled } from '@mui/system';
import { Grid } from '@mui/material';
import { useState, useEffect } from 'react';

//Typing
import { Draft } from 'src/types/common';
import { IAddress, isUS } from 'src/types/address';
import { IFieldErrors } from 'src/types/validation';
import { usStateOptions } from 'src/constants';
import { IRegion } from 'src/services/msReference';

//Processing
import { ChangeManager } from 'src/generic/utils/form';
import { standardize } from 'src/generic/type/modify';

//Components
import { Typography } from 'src/components';
import { Input, Dropdown } from 'src/generic/component/form';
import { FieldNote, FieldLabel } from 'src/generic/component';
import { useMsReference, useReferences } from 'src/services';

const FieldRow = styled(Grid)`
  margin-bottom:10px;
`;

interface AddressFieldProps {
  readOnly?: boolean;
  address?: Draft<IAddress>;
  validating?: boolean;
  errors: IFieldErrors;
  original?: Draft<IAddress>;
  showDiff?: boolean;
  onUpdate?: (address: Draft<IAddress>) => void;
  additionalInfo?: boolean;
  customLabels?: any;
  addressNote?: string;

}

export const AddressFields = ({ additionalInfo, customLabels, readOnly, errors, address, original, showDiff, addressNote = '(P.O. Box addresses are not acceptable)', onUpdate }: AddressFieldProps) => {
  const { regions, countries } = useReferences();
  const refService = useMsReference();
  const [matches, setMatches] = useState<IRegion[]>([]);

  const {
    streetAddr1,
    streetAddr2,
    streetAddr3,
    countryIsoCd,
    city,

    stateCd,
    zip5,
    zip4,

    regionCd,
    province,
    intlPostalCd,
    label,
    contactName,
    contactPhone,
  } = address || {};

  useEffect(() => {
    if (regionCd === null && province) {
      refService.searchProvinces({ countryIsoCd: countryIsoCd, name: province }).then((data) => {
        setMatches(data);
      });
    } else {
      setMatches([]);
    }
  }, [address]);

  const provinces = regions.filter((item) => { return item.countryIsoCd === countryIsoCd; });
  const provinceOptions = provinces.map((option) => ({ key: option.key, label: `${option.label} (${option.key})` }));
  provinceOptions.push({ key: 'null', label: `Other` });

  if (showDiff && !original) throw new Error("Must send the original value when comparing differences.");

  const { getCurrent, isChanged, getChangeColor } = ChangeManager<IAddress>(original || {}, address || {});

  const changed = (updates: Draft<IAddress>) => {
    if (onUpdate) {
      onUpdate(standardize({ ...address, ...updates }));
    }
  };

  const getErrors = (field: string): string[] => {
    //return get(errors, `${field}._errors`, []);
    const error = get(errors, `${field}._errors`, []);
    return Array.isArray(error) ? error : [];
  };

  const hasErrors = (field: string): boolean => {
    return getErrors(field).length > 0 ? true : false;
  };

  return <>
    <FieldRow>
    <Typography variant="body1">Address <FieldNote>{addressNote}</FieldNote></Typography>

      {(showDiff && isChanged('streetAddr1')) &&
        <div><i className={getChangeColor('streetAddr1')}>Current: {getCurrent('streetAddr1') || '(empty)'}</i></div>
      }
      <Input
        disabled={readOnly}
        className={showDiff ? getChangeColor('streetAddr1') : ''}
        validation={hasErrors('streetAddr1') ? 'error' : undefined}
        validationMsg={getErrors('streetAddr1').join("\n")}
        onChange={(e) => {
          const newVal = e.target.value || undefined;
          changed({ streetAddr1: newVal });
        }}
        value={streetAddr1 || ''} />
      {(streetAddr1 || '').length > 0 &&
        <FieldNote>Character Count: {(streetAddr1 || '').length}</FieldNote>
      }
    </FieldRow>
    <FieldRow>
      <Typography variant="body1">Address 2</Typography>
      {(showDiff && isChanged('streetAddr2')) &&
        <div><i className={getChangeColor('streetAddr2')}>Current: {getCurrent('streetAddr2') || '(empty)'}</i></div>
      }
      <Input
        disabled={readOnly}
        className={showDiff ? getChangeColor('streetAddr2') : ''}
        validation={hasErrors('streetAddr2') ? 'error' : undefined}
        validationMsg={getErrors('streetAddr2').join("\n")}
        onChange={(e) => {
          const newVal = e.target.value || undefined;
          changed({ streetAddr2: newVal });
        }}
        value={streetAddr2 || ''} />
      {(streetAddr2 || '').length > 0 &&
        <FieldNote>Character Count: {(streetAddr2 || '').length}</FieldNote>
      }
    </FieldRow>
    <FieldRow>
      <Typography variant="body1">Address 3</Typography>
      {(showDiff && isChanged('streetAddr3')) &&
        <div><i className={getChangeColor('streetAddr3')}>Current: {getCurrent('streetAddr3') || '(empty)'}</i></div>
      }
      <Input
        disabled={readOnly}
        className={showDiff ? getChangeColor('streetAddr3') : ''}
        validation={hasErrors('streetAddr3') ? 'error' : undefined}
        validationMsg={getErrors('streetAddr3').join("\n")}
        onChange={(e) => {
          const newVal = e.target.value || undefined;
          changed({ streetAddr3: newVal });
        }}
        value={streetAddr3 || ''} />
      {(streetAddr3 || '').length > 0 &&
        <FieldNote>Character Count: {(streetAddr3 || '').length}</FieldNote>
      }
    </FieldRow>

    <FieldRow>
      <Typography variant="body1">Country</Typography>
      {(showDiff && isChanged('countryIsoCd')) &&
        <div><i className={getChangeColor('countryIsoCd')}>Current: {getCurrent('countryIsoCd') || '(empty)'}</i></div>
      }
      {countries && <Dropdown values={countries.map((option) => ({ key: option.value, label: option.label }))}
        disabled={readOnly}
        className={showDiff ? getChangeColor('countryIsoCd') : ''}
        validation={hasErrors('countryIsoCd') ? 'error' : undefined}
        validationMsg={getErrors('countryIsoCd').join("\n")}
        onChange={(e) => {
          changed({ countryIsoCd: e.target.value });
        }}
        value={countryIsoCd} />}
    </FieldRow>

    <FieldRow>
      <Typography variant="body1">City</Typography>
      {(showDiff && isChanged('city')) &&
        <div><i className={getChangeColor('city')}>Current: {getCurrent('city') || '(empty)'}</i></div>
      }
      <Input
        disabled={readOnly}
        className={showDiff ? getChangeColor('city') : ''}
        validation={hasErrors('city') ? 'error' : undefined}
        validationMsg={getErrors('city').join("\n")}
        onChange={(e) => {
          const newVal = e.target.value || undefined;
          changed({ city: newVal });
        }}
        value={city || ''} />
        {(city || '').length > 0 &&
        <FieldNote>Character Count: {(city || '').length}</FieldNote>
      }
    </FieldRow>
    {isUS(countryIsoCd || 'US') ?
      <div>
        <FieldRow>
          <Typography variant="body1">State</Typography>
          {(showDiff && isChanged('stateCd')) &&
            <div><i className={getChangeColor('stateCd')}>Current: {getCurrent('stateCd') || '(empty)'}</i></div>
          }
          <Dropdown disabled={readOnly} values={usStateOptions.map((option) => ({ key: option.value, label: option.label }))}
            className={showDiff ? getChangeColor('stateCd') : ''}
            validation={hasErrors('stateCd') ? 'error' : undefined}
            validationMsg={getErrors('stateCd').join("\n")}
            onChange={(e) => {
              const newVal = e.target.value || undefined;
              changed({ stateCd: newVal });
            }}
            value={stateCd || ''} />
        </FieldRow>
        <FieldRow container>
          <Grid item xs={6} style={{ paddingRight: '5px' }}>
            <Typography variant="body1">Zip (5 Digit)</Typography>
            {(showDiff && isChanged('zip5')) &&
              <div><i className={getChangeColor('zip5')}>Current: {getCurrent('zip5') || '(empty)'}</i></div>
            }
            <Input
              disabled={readOnly}
              className={showDiff ? getChangeColor('zip5') : ''}
              validation={hasErrors('zip5') ? 'error' : undefined}
              validationMsg={getErrors('zip5').join("\n")}
              onChange={(e) => {
                const newVal = e.target.value || undefined;
                changed({ zip5: newVal });
              }}
              value={zip5 || ''} />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body1">Zip (4 Digit)</Typography>
            {(showDiff && isChanged('zip4')) &&
              <div><i className={getChangeColor('zip4')}>Current: {getCurrent('zip4') || '(empty)'}</i></div>
            }
            <Input
              disabled={readOnly}
              className={showDiff ? getChangeColor('zip4') : ''}
              validation={hasErrors('zip4') ? 'error' : undefined}
              validationMsg={getErrors('zip4').join("\n")}
              onChange={(e) => {
                const newVal = e.target.value || undefined;
                changed({ zip4: newVal });
              }}
              value={zip4 || ''} />
          </Grid>
        </FieldRow>
      </div>
      :
      <div>
        <FieldRow>
          <Typography variant="body1">Province</Typography>
          {(showDiff && isChanged('regionCd')) &&
            <div><i className={getChangeColor('regionCd')}>Current: {getCurrent('province')} {getCurrent('regionCd') || '(empty)'}</i></div>
          }

          <Dropdown disabled={readOnly}
            values={provinceOptions}
            className={showDiff ? getChangeColor('regionCd') : ''}
            validation={hasErrors('regionCd') ? 'error' : undefined}
            validationMsg={getErrors('regionCd').join("\n")}
            onChange={(e) => {
              const newVal = e.target.value === 'null' ? null : e.target.value || undefined;
              changed({
                regionCd: newVal,
                province: provinceOptions.find((item) => item.key === newVal)?.label || undefined
              });
            }}
            value={regionCd === null ? 'null' : regionCd || ''} />
          {regionCd === null &&
            <FieldNote>
              {matches.length > 0 &&
                <>
                  Possible Matches:&nbsp;
                  {matches.map((item) => {
                    return item.label;
                  }).join(', ')}
                </>
              }
              <Input
                style={{ marginTop: '4px' }}
                placeholder='Enter a Province Name...'
                disabled={readOnly}
                className={showDiff ? getChangeColor('province') : ''}
                validation={hasErrors('province') ? 'error' : undefined}
                validationMsg={getErrors('province').join("\n")}
                onChange={(e) => {
                  const newVal = e.target.value || undefined;
                  changed({ province: newVal });
                }}
                value={province || ''} />

            </FieldNote>
          }
        </FieldRow>
        <FieldRow>
          <Typography variant="body1">Postal Code</Typography>
          {(showDiff && isChanged('intlPostalCd')) &&
            <div><i className={getChangeColor('intlPostalCd')}>Current: {getCurrent('intlPostalCd') || '(empty)'}</i></div>
          }
          <Input
            disabled={readOnly}
            className={showDiff ? getChangeColor('intlPostalCd') : ''}
            validation={hasErrors('intlPostalCd') ? 'error' : undefined}
            validationMsg={getErrors('intlPostalCd').join("\n")}
            onChange={(e) => {
              const newVal = e.target.value || undefined;
              changed({ intlPostalCd: newVal });
            }}
            value={intlPostalCd || ''} />
        </FieldRow>
      </div>
    }
    {additionalInfo &&
      <>
        <FieldRow>
          <FieldLabel>
            {customLabels?.label || 'Label'}
          </FieldLabel>
          {(showDiff && isChanged('label')) &&
              <div><i className={getChangeColor('label')}>Current: {getCurrent('label') || '(empty)'}</i></div>
          }
          <Input
              disabled={readOnly}
              className={showDiff ? getChangeColor('label') : ''}
              validation={hasErrors('label') ? 'error' : undefined}
              validationMsg={getErrors('label').join("\n")}
              onChange={(e) => {
                const newVal = e.target.value || undefined;
                changed({label: newVal});
              }}
              value={label || ''}/>
          {(label || '').length > 0 &&
              <FieldNote>Character Count: {(label || '').length}</FieldNote>
          }
        </FieldRow>
        <FieldRow>
          <FieldLabel>
            {customLabels?.contactName || 'Contact Name'}
          </FieldLabel>
          {(showDiff && isChanged('contactName')) &&
              <div><i className={getChangeColor('contactName')}>Current: {getCurrent('contactName') || '(empty)'}</i>
              </div>
          }
          <Input
              disabled={readOnly}
              className={showDiff ? getChangeColor('contactName') : ''}
              validation={hasErrors('contactName') ? 'error' : undefined}
              validationMsg={getErrors('contactName').join("\n")}
              onChange={(e) => {
                const newVal = e.target.value || undefined;
                changed({contactName: newVal});
              }}
              value={contactName || ''}/>
          {(contactName || '').length > 0 &&
              <FieldNote>Character Count: {(contactName || '').length}</FieldNote>
          }
        </FieldRow>
        <FieldRow>
          <FieldLabel>
            {customLabels?.contactPhone || 'Contact Phone'}
          </FieldLabel>
          {(showDiff && isChanged('contactPhone')) &&
              <div><i className={getChangeColor('contactPhone')}>Current: {getCurrent('contactPhone') || '(empty)'}</i>
              </div>
          }
          <Input
              disabled={readOnly}
              className={showDiff ? getChangeColor('contactPhone') : ''}
              validation={hasErrors('contactPhone') ? 'error' : undefined}
              validationMsg={getErrors('contactPhone').join("\n")}
              onChange={(e) => {
                const newVal = e.target.value || undefined;
                changed({contactPhone: newVal});
              }}
              value={contactPhone || ''}/>
          {(contactPhone || '').length > 0 &&
              <FieldNote>Character Count: {(contactPhone || '').length}</FieldNote>
          }
        </FieldRow>
      </>
    }
  </>;
};


export const formatAddress = (address: Draft<IAddress>) => {
  return <>
    {address.label &&
      <div>{address.label}</div>
    }
    <div>{address.streetAddr1}</div>
    {address.streetAddr2 &&
      <div>{address.streetAddr2}</div>
    }
    {address.streetAddr3 &&
      <div>{address.streetAddr3}</div>
    }
    {address.countryIsoCd === 'US' ?
      <div>{address.city},&nbsp;{address.stateCd} {address.zip5} {address.countryIsoCd}</div> :
      <div>{address.city},&nbsp;{address.province} {address.intlPostalCd} {address.countryIsoCd}</div>
    }
    {address.contactName &&
      <div>{address.contactName}</div>
    }
    {address.contactPhone &&
      <div>{address.contactPhone}</div>
    }
    <div></div>
  </>;
};
